import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { Alert, RefreshControl } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useMutation } from '@apollo/client';

import { AddressDetail } from '../address';
import { DetailContainer } from '../../containers';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { TypographyHeading } from '../../components';
import { Card, SectionTitle } from '../../design/components';
import PageHeading from '../../design/components/PageHeading';
import { TextInputEditInPlace } from '../../components/input';
import { useUserContext } from '../../../common/utils/context';
import { ROLE_PERMISSIONS } from '../../../common/constants';
import ClientSelector from '../../components/selector/ClientSelector';
import PersonSelector from '../../components/selector/PersonSelector';
import { MapLink } from '../map/MapLink';
import { MODIFY_ADDRESS } from '../../../common/graphql/address';
import { CREATE_MEDIA, MODIFY_MEDIA } from '../../../common/graphql/media';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useOfflineQueue } from '../../offline';
import AccessDirectionsSection from '../../components/access/AccessDirectionsSection';
import LocationSignInDetails from '../../components/access/LocationSignInDetails';

const isRemoteUrl = url => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));
const getPhotoId = photo => photo?.id || photo?._id || photo;

const LocationDetail = ({
	captureMedia,
	capturedMediaLink,
	refetch,
	data: location,
	errors,
	hideDirections = false,
	modifyItem: modifyLocation,
	processingMedia,
}) => {
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const [handledCapturedMediaLink, setHandledCapturedMediaLink] = useState(null);

	const { hasPermission } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const [createMedia] = useMutation(CREATE_MEDIA);
	const [modifyMedia] = useMutation(MODIFY_MEDIA);

	const canEditLocation = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_LOCATIONS);

	const onChangeLocation = useCallback(field => value => modifyLocation({ [field]: value ? value.id : null }), [modifyLocation]);
	const visualDirectionPhotos = useMemo(() => (
		location.visualDirectionPhotos || []
	), [location.visualDirectionPhotos]);
	const visualDirectionPhotoUrls = useMemo(() => (
		(location.visualDirectionPhotoUrls || []).filter(isRemoteUrl)
	), [location.visualDirectionPhotoUrls]);
	const visualDirectionPhotoIds = useMemo(() => (
		visualDirectionPhotos.map(getPhotoId).filter(Boolean)
	), [visualDirectionPhotos]);

	const addVisualDirectionPhoto = useCallback(() => {
		if (!isOnline) {
			Alert.alert('Internet connection required', 'Visual direction photos need to upload before they can be saved to a location.');
			return;
		}

		if (!processingMedia) {
			captureMedia();
		}
	}, [captureMedia, isOnline, processingMedia]);

	const removeVisualDirectionPhoto = useCallback((photo) => {
		const photoId = getPhotoId(photo);
		Alert.alert(
			'Remove visual direction photo?',
			'',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Remove',
					style: 'destructive',
					onPress: () => modifyLocation({
						visualDirectionPhotos: photo?.legacyUrl
							? visualDirectionPhotoIds
							: visualDirectionPhotoIds.filter(currentPhotoId => currentPhotoId !== photoId),
						visualDirectionPhotoUrls: visualDirectionPhotoUrls.filter(currentPhotoUrl => currentPhotoUrl !== photo?.link),
					}),
				},
			],
		);
	}, [modifyLocation, visualDirectionPhotoIds, visualDirectionPhotoUrls]);

	const updateVisualDirectionPhotoComment = useCallback(async (photo, comment) => {
		const photoId = getPhotoId(photo);
		if (!photoId || photo.legacyUrl) {
			return;
		}

		try {
			await modifyMedia({
				variables: {
					input: {
						id: photoId,
						comment: comment || null,
					},
				},
			});
		}
		catch (error) {
			Alert.alert('Description not saved', 'Unable to save the visual direction photo description.');
		}
	}, [modifyMedia]);

	useEffect(() => {
		if (!capturedMediaLink || capturedMediaLink === handledCapturedMediaLink) {
			return;
		}

		setHandledCapturedMediaLink(capturedMediaLink);

		if (!isRemoteUrl(capturedMediaLink)) {
			Alert.alert('Photo not saved', 'Visual direction photos need an internet connection so the photo can upload before saving.');
			return;
		}

		if (
			!location.id ||
			visualDirectionPhotos.some(photo => photo?.link === capturedMediaLink) ||
			visualDirectionPhotoUrls.includes(capturedMediaLink)
		) {
			return;
		}

		(async () => {
			try {
				const result = await createMedia({
					variables: {
						input: {
							location: location.id,
							link: capturedMediaLink,
						},
					},
				});
				const mediaId = result?.data?.createMedia?.id;
				if (!mediaId) {
					Alert.alert('Photo not saved', 'Unable to save the visual direction photo.');
					return;
				}

				await modifyLocation({
					visualDirectionPhotos: [...visualDirectionPhotoIds, mediaId],
				});
			}
			catch (error) {
				Alert.alert('Photo not saved', 'Unable to save the visual direction photo.');
			}
		})();
	}, [
		capturedMediaLink,
		createMedia,
		handledCapturedMediaLink,
		location.id,
		modifyLocation,
		visualDirectionPhotoIds,
		visualDirectionPhotoUrls,
		visualDirectionPhotos,
	]);

	const styles = useThemedStyles(createStyles);

	return (
		<KeyboardAwareScrollView
			style={styles.scrollView}
			contentContainerStyle={styles.scrollContent}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			<PageHeading
				title={`${location.name}`}
			/>
			<Card style={styles.section}>
				<SectionTitle title="Information" />
				<TypographyHeading
					fieldLabel={true}
					label="Name"
				/>
				<TextInputEditInPlace
					value={location.name}
					placeholder="No Name"
					field="name"
					disabled={!canEditLocation}
					onConfirm={onChangeLocation('name')}
					error={errors.client?.name}
				/>
				<TypographyHeading
					fieldLabel={true}
					label="Client"
				/>
				<ClientSelector
					initialValue={location.client}
					onChange={onChangeLocation('client')}
					disabled={!canEditLocation}
				/>

				<TypographyHeading
					fieldLabel={true}
					label="Primary contact"
				/>
				<PersonSelector
					initialValue={location.person}
					onChange={onChangeLocation('person')}
					disabled={!canEditLocation}
				/>

				<TypographyHeading
					fieldLabel={true}
					label="Secondary contact"
				/>
				<PersonSelector
					initialValue={location.secondaryPerson}
					onChange={onChangeLocation('secondaryPerson')}
					disabled={!canEditLocation}
				/>

				<LocationSignInDetails
					location={location}
					canEdit={canEditLocation}
					onConfirm={modifyLocation}
				/>
			</Card>

			{!hideDirections && (
				<Card style={styles.section}>
					<SectionTitle title="Location directions" />
					<TypographyHeading
						fieldLabel={true}
						label="Access hours"
					/>
					<TextInputEditInPlace
						value={location.accessHours}
						placeholder="None specified"
						field="accessHours"
						disabled={!canEditLocation}
						onConfirm={modifyLocation}
					/>

					<TypographyHeading
						fieldLabel={true}
						label="Directions"
					/>
					<TextInputEditInPlace
						value={location.directions}
						placeholder="No directions"
						field="directions"
						disabled={!canEditLocation}
						onConfirm={modifyLocation}
						style={styles.multilineInput}
						multiline={true}
						numberOfLines={4}
					/>
					<AccessDirectionsSection
						showTitle={false}
						locationTitle="Visual directions"
						showEmpty={true}
						showEmptyPhotos={true}
						showDirectionsText={false}
						location={location}
						canEditLocation={canEditLocation}
						onAddLocationPhoto={addVisualDirectionPhoto}
						onRemoveLocationPhoto={removeVisualDirectionPhoto}
						onUpdateLocationPhotoComment={updateVisualDirectionPhotoComment}
					/>
				</Card>
			)}
			<Card style={styles.section}>
				<SectionTitle title="Address" />
				<AddressDetail
					id={location.addressObj?.id}
					data={location.addressObj}
					modifyMutation={MODIFY_ADDRESS}
				/>
				{!hideDirections && <MapLink location={location} />}
			</Card>
		</KeyboardAwareScrollView>
	);
};

const createStyles = ({ ui: { space, layout } }) => ({
	scrollView: {
		flex: 1,
	},
	scrollContent: {
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.xl,
		gap: space.gutter,
	},
	section: {
		gap: space.xs,
	},
	multilineInput: {
		minHeight: 100,
		textAlignVertical: 'top', // Keep placeholder/text anchored at the top edge
	},
});

export default compose(DetailContainer, withMediaPicker)(LocationDetail);
